import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getArticleBySlug } from '../src/data/articles';
import { getProductById } from '../src/data/products';

const BlogArticle: React.FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return <Navigate to="/dossier" replace />;
  }

  // Generate article schema markup for SEO
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.metaDescription,
    image: `https://borninyu.com${article.featuredImage}`,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Born in YU',
      logo: {
        '@type': 'ImageObject',
        url: 'https://borninyu.com/og-image.jpg',
      },
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
  };

  const relatedProducts = article.relatedProducts
    .map((id) => getProductById(id))
    .filter((p) => p !== undefined);

  return (
    <>
      <Helmet>
        <title>{article.title} | Born in YU Dossier</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="keywords" content={article.seoKeywords.join(', ')} />
        <meta name="author" content={article.author} />
        <link rel="canonical" href={`https://borninyu.com/dossier/${article.slug}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:image" content={`https://borninyu.com${article.featuredImage}`} />
        <meta property="og:url" content={`https://borninyu.com/dossier/${article.slug}`} />
        <meta property="article:published_time" content={article.publishDate} />
        <meta property="article:author" content={article.author} />
        <meta property="article:section" content={article.category} />
        {article.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.metaDescription} />
        <meta name="twitter:image" content={`https://borninyu.com${article.featuredImage}`} />

        {/* Article Schema */}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <main className="min-h-screen bg-white">
        {/* Breadcrumbs */}
        <div className="bg-black text-white p-4">
          <div className="max-w-4xl mx-auto flex items-center gap-2 text-xs font-typewriter uppercase">
            <Link to="/" className="hover:text-[#C41E3A] transition-colors">
              {t('blog.navigation.home')}
            </Link>
            <span>/</span>
            <Link to="/dossier" className="hover:text-[#C41E3A] transition-colors">
              {t('blog.navigation.dossier')}
            </Link>
            <span>/</span>
            <span className="text-gray-400">{t('blog.navigation.article') || 'Article'}</span>
          </div>
        </div>

        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-6 py-12 md:px-12 md:py-16">
          {/* Category & Date */}
          <div className="flex items-center justify-between mb-6">
            <div className="border-2 border-black bg-[#C41E3A] text-white px-4 py-2 text-xs font-black uppercase">
              {article.category}
            </div>
            <div className="text-xs font-typewriter uppercase text-gray-500">
              {new Date(article.publishDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
              {' • '}
              {article.readTime}
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-4">
            {article.title}
          </h1>

          {/* Subtitle */}
          <p className="text-xl font-bold text-gray-700 mb-6">{article.subtitle}</p>

          {/* Author & Tags */}
          <div className="flex items-center justify-between pb-8 mb-8 border-b-2 border-black">
            <div className="text-sm font-bold uppercase">
              By <span className="text-[#C41E3A]">{article.author}</span>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 border border-black text-xs font-black uppercase bg-gray-100"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Featured Image - Only show if image exists */}
          <div className="mb-12 border-4 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] bg-gray-200 relative overflow-hidden">
            <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
              {/* Placeholder - shown by default, hidden when image loads */}
              <div className="absolute inset-0 flex items-center justify-center z-0" id="image-placeholder">
                <div className="text-center p-8">
                  <div className="text-6xl font-black text-gray-500 opacity-20 mb-4">YU</div>
                  <div className="text-xs font-black uppercase text-gray-600">{t('blog.imagePlaceholder')}</div>
                </div>
              </div>
              {/* Actual image - loads on top of placeholder */}
              <img
                src={article.featuredImage}
                alt={article.featuredImageAlt}
                className="w-full h-full object-cover relative z-10"
                onLoad={(e) => {
                  const placeholder = e.currentTarget.parentElement?.querySelector('#image-placeholder');
                  if (placeholder) placeholder.classList.add('hidden');
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>

          {/* Article Content */}
          <div
            className="article-content prose prose-lg max-w-none
              prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-headings:text-black
              prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-8 prose-h2:pb-4 prose-h2:border-l-8 prose-h2:border-[#C41E3A] prose-h2:pl-6 prose-h2:leading-tight
              prose-h3:text-xl prose-h3:mt-12 prose-h3:mb-4 prose-h3:font-black prose-h3:leading-tight
              prose-p:text-base prose-p:text-gray-800 prose-p:leading-relaxed prose-p:mb-6 prose-p:normal-case
              prose-a:text-[#C41E3A] prose-a:font-bold prose-a:no-underline hover:prose-a:underline
              prose-strong:font-black prose-strong:text-black prose-strong:normal-case
              prose-ul:my-8 prose-ul:list-disc prose-ul:pl-6 prose-li:mb-3 prose-li:text-gray-800 prose-li:leading-relaxed
              prose-img:border-2 prose-img:border-black prose-img:shadow-lg
              [&_br]:hidden"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-16 pt-12 border-t-4 border-black">
              <h2 className="text-3xl font-black uppercase tracking-tighter mb-8 border-l-4 border-[#C41E3A] pl-4">
                {t('blog.relatedProducts')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    className="border-3 border-black bg-white hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all p-6 group"
                  >
                    <div className="aspect-square mb-4 overflow-hidden border-2 border-black">
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                      />
                    </div>
                    <h3 className="text-xl font-black uppercase mb-2">{product.name}</h3>
                    <p className="text-sm text-gray-700 mb-3">{product.description}</p>
                    <div className="text-2xl font-black text-[#C41E3A]">
                      ${product.price?.toFixed(2)}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back to Dossier */}
          <div className="mt-16 text-center">
            <Link
              to="/dossier"
              className="inline-block px-8 py-3 border-2 border-black bg-white text-black font-black uppercase text-sm hover:bg-black hover:text-white transition-all"
            >
              ← {t('blog.backToDossier')}
            </Link>
          </div>
        </article>
      </main>
    </>
  );
};

export default BlogArticle;
